// Spielfigur als Skelett aus gemalten Teilen (Canvas-Texturen) mit prozeduraler Lauf-/Idle-/Sprech-Animation.
import { Container, Sprite, Texture } from 'pixi.js';
import { P } from '../art/palette.ts';
import { makeCanvas, rgba, shade, roundRect, type Ctx } from '../art/paint.ts';
import { OUTFITS_CH3 } from './outfits-ch3.ts';
import { OUTFITS_CH4 } from './outfits-ch4.ts';
import { OUTFITS_CH5 } from './outfits-ch5.ts';

export interface Outfit {
  coat: string;
  pants: string;
  hair: string;
  skin: string;
  boots: string;
  hat?: string;
  beard?: string;
  scarf?: string;
}

export const OUTFITS: Record<string, Outfit> = {
  mara: { coat: P.amber1, pants: P.night1, hair: P.red1, skin: P.skin2, boots: P.wood0, scarf: P.sea1 },
  hinnerk: { coat: P.night1, pants: P.iron0, hair: P.plaster2, skin: P.skin1, boots: P.ink, hat: P.night0, beard: P.plaster2 },
  gesa: { coat: P.red1, pants: P.wood1, hair: P.paper, skin: P.skin2, boots: P.wood0, scarf: P.green2 },
  jan: { coat: P.green1, pants: P.wood1, hair: P.wood1, skin: P.skin1, boots: P.ink, scarf: P.red1, beard: P.wood1 },
  ole: { coat: P.night2, pants: P.iron0, hair: P.white, skin: P.skin1, boots: P.ink, hat: P.night1, beard: P.white },
  brakenhoff: { coat: P.ink, pants: P.ink, hair: P.iron2, skin: P.skin2, boots: P.ink, scarf: P.red0 },
};
// Outfits der Kapitel 3–5 (je Kapitel eine Datei)
Object.assign(OUTFITS, OUTFITS_CH3, OUTFITS_CH4, OUTFITS_CH5);

function tex(w: number, h: number, draw: (c: Ctx) => void): Texture {
  const c = makeCanvas(w, h);
  draw(c.getContext('2d')!);
  return Texture.from(c);
}

function part(t: Texture, ax: number, ay: number): Sprite {
  const s = new Sprite(t);
  s.anchor.set(ax, ay);
  return s;
}

export class Character extends Container {
  private hips = new Container();
  private torso = new Container();
  private head = new Container();
  private legs: { thigh: Container; shin: Container }[] = [];
  private arms: { upper: Container; fore: Container }[] = [];
  private t = 0;
  idleSeed = 0;
  private lid!: Sprite;
  private mouth!: Sprite;
  private nextBlink = 1 + Math.random() * 3;
  walking = false;
  talking = false;
  facing: 'left' | 'right' = 'right';
  private body = new Container();

  constructor(public outfit: Outfit) {
    super();
    const o = outfit;
    // Maße in Pixel bei Skalierung 1 (Figur ~360 hoch)
    const legTex = (back: boolean) =>
      tex(40, 100, (c) => {
        c.fillStyle = back ? shade(o.pants, -0.3) : o.pants;
        roundRect(c, 6, 0, 28, 100, 12);
        c.fill();
        c.fillStyle = rgba(P.amber3, back ? 0 : 0.12);
        c.fillRect(8, 4, 6, 80);
      });
    const shinTex = (back: boolean) =>
      tex(64, 104, (c) => {
        c.fillStyle = back ? shade(o.pants, -0.3) : o.pants;
        roundRect(c, 10, 0, 26, 70, 10);
        c.fill();
        // Stiefel
        c.fillStyle = back ? shade(o.boots, -0.3) : o.boots;
        roundRect(c, 8, 60, 30, 38, 8);
        c.fill();
        roundRect(c, 8, 84, 50, 18, 8);
        c.fill();
        c.fillStyle = rgba(P.amber3, back ? 0.05 : 0.2);
        c.fillRect(12, 64, 22, 4);
      });
    const torsoTex = tex(110, 160, (c) => {
      // Mantel (Friesennerz)
      c.fillStyle = shade(o.coat, -0.15);
      c.beginPath();
      c.moveTo(22, 20);
      c.quadraticCurveTo(55, 4, 88, 20);
      c.lineTo(98, 150);
      c.quadraticCurveTo(55, 160, 12, 150);
      c.closePath();
      c.fill();
      const g = c.createLinearGradient(0, 0, 110, 0);
      g.addColorStop(0, rgba(P.night0, 0.25));
      g.addColorStop(0.5, rgba(P.amber3, 0.2));
      g.addColorStop(1, rgba(P.night0, 0.35));
      c.fillStyle = g;
      c.fill();
      // Knöpfe / Knebel
      c.fillStyle = shade(o.coat, -0.5);
      for (let i = 0; i < 4; i++) c.fillRect(70, 40 + i * 26, 12, 4);
      // Tasche
      c.strokeStyle = shade(o.coat, -0.4);
      c.lineWidth = 2;
      c.strokeRect(30, 100, 30, 22);
      // Schal
      if (o.scarf) {
        c.fillStyle = o.scarf;
        roundRect(c, 24, 6, 64, 24, 10);
        c.fill();
        c.fillRect(64, 20, 14, 46);
      }
    });
    const headTex = tex(100, 110, (c) => {
      // Hals
      c.fillStyle = shade(o.skin, -0.2);
      c.fillRect(40, 80, 22, 26);
      // Kopf
      c.fillStyle = o.skin;
      c.beginPath();
      c.ellipse(52, 56, 30, 36, 0, 0, Math.PI * 2);
      c.fill();
      // Nase
      c.beginPath();
      c.moveTo(78, 52);
      c.lineTo(88, 66);
      c.lineTo(76, 68);
      c.fill();
      // Wange
      c.fillStyle = rgba(P.red2, 0.3);
      c.beginPath();
      c.arc(66, 70, 7, 0, Math.PI * 2);
      c.fill();
      // Auge
      c.fillStyle = P.ink;
      c.beginPath();
      c.ellipse(68, 52, 3, 4.5, 0, 0, Math.PI * 2);
      c.fill();
      c.strokeStyle = shade(o.hair, -0.3);
      c.lineWidth = 3;
      c.beginPath();
      c.moveTo(60, 42);
      c.lineTo(74, 41);
      c.stroke();
      // Mund
      c.strokeStyle = shade(o.skin, -0.45);
      c.lineWidth = 2;
      c.beginPath();
      c.moveTo(66, 80);
      c.lineTo(76, 79);
      c.stroke();
      // Haare
      c.fillStyle = o.hair;
      c.beginPath();
      c.ellipse(46, 36, 32, 22, -0.2, Math.PI * 0.9, Math.PI * 2.1);
      c.fill();
      c.beginPath();
      c.ellipse(28, 54, 14, 28, 0.1, 0, Math.PI * 2);
      c.fill();
      if (!o.hat) {
        // Dutt
        c.beginPath();
        c.arc(20, 30, 14, 0, Math.PI * 2);
        c.fill();
      }
      c.fillStyle = rgba(P.amber3, 0.25);
      c.beginPath();
      c.ellipse(52, 24, 16, 5, -0.2, 0, Math.PI * 2);
      c.fill();
      if (o.beard) {
        c.fillStyle = o.beard;
        c.beginPath();
        c.ellipse(60, 80, 26, 18, 0, 0, Math.PI);
        c.fill();
      }
      if (o.hat) {
        c.fillStyle = o.hat;
        roundRect(c, 18, 10, 66, 26, 8);
        c.fill();
        c.fillRect(14, 30, 76, 8);
        c.fillStyle = rgba(P.amber3, 0.2);
        c.fillRect(20, 12, 50, 4);
      }
    });
    const upperArmTex = (back: boolean) =>
      tex(36, 80, (c) => {
        c.fillStyle = back ? shade(o.coat, -0.4) : o.coat;
        roundRect(c, 4, 0, 28, 80, 13);
        c.fill();
        c.fillStyle = rgba(P.amber3, back ? 0 : 0.2);
        c.fillRect(8, 6, 5, 60);
      });
    const foreArmTex = (back: boolean) =>
      tex(36, 86, (c) => {
        c.fillStyle = back ? shade(o.coat, -0.4) : shade(o.coat, -0.05);
        roundRect(c, 5, 0, 26, 64, 12);
        c.fill();
        c.fillStyle = back ? shade(o.skin, -0.3) : o.skin;
        c.beginPath();
        c.ellipse(18, 72, 11, 13, 0, 0, Math.PI * 2);
        c.fill();
      });

    // Hierarchie aufbauen: body (Spiegelung) > hips > [legs, torso > [arms, head]]
    this.addChild(this.body);
    this.body.addChild(this.hips);
    this.hips.y = -196;
    for (const back of [true, false]) {
      const thigh = new Container();
      thigh.addChild(part(legTex(back), 0.5, 0.06));
      const shin = new Container();
      shin.addChild(part(shinTex(back), 0.36, 0.0));
      shin.y = 90;
      thigh.addChild(shin);
      thigh.x = back ? -6 : 6;
      this.hips.addChild(thigh);
      this.legs.push({ thigh, shin });
    }
    this.torso.addChild(part(torsoTex, 0.5, 0.95));
    this.torso.y = 10;
    // hinterer Arm hinter dem Torso
    const mkArm = (back: boolean) => {
      const upper = new Container();
      upper.addChild(part(upperArmTex(back), 0.5, 0.1));
      const fore = new Container();
      fore.addChild(part(foreArmTex(back), 0.5, 0.05));
      fore.y = 66;
      upper.addChild(fore);
      upper.y = -130;
      upper.x = back ? -4 : 4;
      return { upper, fore };
    };
    const backArm = mkArm(true);
    this.hips.addChild(this.torso);
    this.torso.addChildAt(backArm.upper, 0);
    this.head.addChild(part(headTex, 0.48, 0.92));
    // Lid (Blinzeln) und Mund (Sprechen) als eigene Teile über dem gemalten Gesicht
    this.lid = new Sprite(tex(10, 12, (c) => {
      c.fillStyle = o.skin;
      c.beginPath();
      c.ellipse(5, 6, 4.5, 6, 0, 0, Math.PI * 2);
      c.fill();
    }));
    this.lid.anchor.set(0.5);
    this.lid.position.set(68 - 48, 52 - 101.2);
    this.lid.visible = false;
    this.mouth = new Sprite(tex(14, 10, (c) => {
      c.fillStyle = shade(o.skin, -0.55);
      c.beginPath();
      c.ellipse(7, 5, 5.5, 4, 0, 0, Math.PI * 2);
      c.fill();
    }));
    this.mouth.anchor.set(0.5);
    this.mouth.position.set(71 - 48, 80 - 101.2);
    this.mouth.visible = false;
    this.head.addChild(this.lid, this.mouth);
    this.head.y = -138;
    this.torso.addChild(this.head);
    const frontArm = mkArm(false);
    this.torso.addChild(frontArm.upper);
    this.arms.push(backArm, frontArm);
  }

  setFacing(f: 'left' | 'right') {
    this.facing = f;
    this.body.scale.x = f === 'left' ? -1 : 1;
  }

  update(dt: number) {
    this.t += dt;
    const t = this.t + this.idleSeed;
    // Blinzeln alle paar Sekunden (0,12 s), Mund bewegt sich beim Sprechen
    this.nextBlink -= dt;
    if (this.nextBlink < 0) {
      this.lid.visible = this.nextBlink > -0.12;
      if (this.nextBlink <= -0.12) this.nextBlink = 2 + Math.random() * 4;
    }
    this.mouth.visible = this.talking;
    if (this.talking) this.mouth.scale.y = 0.3 + Math.abs(Math.sin(t * 13)) * 0.9;
    if (this.walking) {
      const ph = t * 9;
      this.legs.forEach((l, i) => {
        const p = ph + (i ? Math.PI : 0);
        l.thigh.rotation = Math.sin(p) * 0.5;
        l.shin.rotation = Math.max(0, -Math.cos(p)) * 0.75;
      });
      this.arms.forEach((a, i) => {
        const p = ph + (i ? 0 : Math.PI);
        a.upper.rotation = Math.sin(p) * 0.45;
        a.fore.rotation = -0.25 - Math.max(0, Math.sin(p)) * 0.3;
      });
      this.hips.y = -196 - Math.abs(Math.cos(ph)) * 6;
      this.torso.rotation = 0.05;
      this.head.rotation = 0;
    } else {
      // Idle: atmen, leichte Gewichtsverlagerung
      this.legs.forEach((l, i) => {
        l.thigh.rotation = i ? 0.04 : -0.06;
        l.shin.rotation = 0.03;
      });
      this.arms.forEach((a, i) => {
        a.upper.rotation = (i ? 0.08 : -0.06) + Math.sin(t * 1.6) * 0.02;
        a.fore.rotation = -0.12;
      });
      this.hips.y = -196 + Math.sin(t * 1.6) * 1.5;
      this.torso.rotation = Math.sin(t * 0.8) * 0.01;
      this.head.rotation = this.talking ? Math.sin(t * 11) * 0.05 + 0.02 : Math.sin(t * 0.7) * 0.03;
      if (this.talking) this.arms[1].upper.rotation = -0.35 + Math.sin(t * 4) * 0.15;
    }
  }

  /** Kopfhöhe (lokal, unskaliert) für Sprechblasen */
  get headTop(): number {
    return -430;
  }
}
